import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, Brain, BarChart3, CreditCard, Settings, Shield, Building2, LogOut, ChevronLeft, Menu, Activity, Stethoscope, CalendarDays, FileText, Heart, Plug, Map, Code, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useState } from 'react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['super_admin', 'hospital_admin', 'clinic_admin', 'doctor', 'nurse'] },
  { path: '/patients', label: 'Patients', icon: Users, roles: ['super_admin', 'hospital_admin', 'clinic_admin', 'doctor', 'nurse'] },
  { path: '/triage', label: 'AI Triage', icon: Brain, roles: ['super_admin', 'hospital_admin', 'clinic_admin', 'doctor', 'nurse'] },
  { path: '/doctors', label: 'Doctors', icon: Stethoscope, roles: ['super_admin', 'hospital_admin', 'clinic_admin', 'doctor', 'nurse'] },
  { path: '/appointments', label: 'Appointments', icon: CalendarDays, roles: ['super_admin', 'hospital_admin', 'clinic_admin', 'doctor', 'nurse'] },
  { path: '/surveys', label: 'Pre-Visit Forms', icon: FileText, roles: ['super_admin', 'hospital_admin', 'clinic_admin'] },
  { path: '/patient-journey', label: 'Patient Journey', icon: Map, roles: ['super_admin', 'hospital_admin', 'clinic_admin', 'doctor', 'nurse'] },
  { path: '/follow-ups', label: 'Follow-Ups', icon: Heart, roles: ['super_admin', 'hospital_admin', 'clinic_admin', 'doctor', 'nurse'] },
  { path: '/analytics', label: 'Analytics', icon: BarChart3, roles: ['super_admin', 'hospital_admin', 'clinic_admin'] },
  { path: '/billing', label: 'Billing', icon: CreditCard, roles: ['super_admin', 'hospital_admin', 'clinic_admin'] },
  { path: '/integrations', label: 'EHR Integration', icon: Plug, roles: ['super_admin', 'hospital_admin', 'clinic_admin'] },
  { path: '/widget-config', label: 'Chat Widget', icon: Code, roles: ['super_admin', 'hospital_admin', 'clinic_admin'] },
  { path: '/whatsapp', label: 'WhatsApp', icon: MessageCircle, roles: ['super_admin', 'hospital_admin', 'clinic_admin'] },
  { path: '/tenants', label: 'Tenants', icon: Building2, roles: ['super_admin'] },
  { path: '/settings', label: 'Settings', icon: Settings, roles: ['super_admin', 'hospital_admin', 'clinic_admin'] },
  { path: '/audit-logs', label: 'Audit Logs', icon: Shield, roles: ['super_admin', 'hospital_admin', 'clinic_admin'] },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const filteredNav = navItems.filter(item => item.roles.includes(user?.role));
  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';

  const roleLabel = {
    super_admin: 'Super Admin',
    hospital_admin: 'Hospital Admin',
    clinic_admin: 'Clinic Admin',
    doctor: 'Doctor',
    nurse: 'Nurse'
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        data-testid="sidebar"
        className={`flex flex-col bg-white border-r border-slate-200 transition-all duration-300 ${collapsed ? 'w-[68px]' : 'w-[260px]'} h-screen`}
      >
        <div className="flex items-center gap-3 px-4 h-16 border-b border-slate-100">
          <div className="w-8 h-8 rounded-lg bg-sky-600 flex items-center justify-center flex-shrink-0">
            <Activity className="w-5 h-5 text-white" />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-slate-900 tracking-tight" style={{fontFamily: 'Manrope'}}>engagingly.ai</h1>
              <p className="text-[9px] text-slate-400 leading-tight -mt-0.5">Where health meets intelligent engagement</p>
            </div>
          )}
          <Button
            data-testid="sidebar-toggle"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-slate-400 hover:text-slate-600"
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? <Menu className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
          {filteredNav.map((item) => {
            const isActive = location.pathname === item.path || (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            const Icon = item.icon;
            const btn = (
              <button
                key={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => navigate(item.path)}
                className={`sidebar-nav-item w-full ${isActive ? 'active' : 'text-slate-600'}`}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-sky-700' : 'text-slate-400'}`} />
                {!collapsed && <span>{item.label}</span>}
              </button>
            );
            return collapsed ? (
              <Tooltip key={item.path}>
                <TooltipTrigger asChild>{btn}</TooltipTrigger>
                <TooltipContent side="right">{item.label}</TooltipContent>
              </Tooltip>
            ) : btn;
          })}
        </nav>

        <div className="px-3 pb-4">
          <Separator className="mb-4" />
          <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
            <Avatar className="h-9 w-9 bg-sky-100 flex-shrink-0">
              <AvatarFallback className="bg-sky-100 text-sky-700 text-sm font-semibold">{initials}</AvatarFallback>
            </Avatar>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{roleLabel[user?.role] || user?.role}</p>
              </div>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  data-testid="logout-btn"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-400 hover:text-red-500 flex-shrink-0"
                  onClick={logout}
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Sign out</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </aside>
    </TooltipProvider>
  );
}
